export const MAX_PHOTOS = 5;

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;
const PASSTHROUGH_BYTES = 200 * 1024;
const MAX_SOURCE_BYTES = 15 * 1024 * 1024;

// Must stay within the backend's @Size(max = 1000000) on the photo1..photo10 fields.
const MAX_ENCODED_CHARS = 1000000;

export function isPhotoDataUrl(value) {
  return typeof value === "string" && value.startsWith("data:image/");
}

export function photoSlotKey(index) {
  return `photo${index + 1}`;
}

export function photoSlots(row, max = MAX_PHOTOS) {
  const slots = [];
  for (let i = 0; i < max; i++) {
    const value = row?.[photoSlotKey(i)];
    if (isPhotoDataUrl(value)) slots.push(value);
  }
  return slots;
}

export function thumbnailForRow(row, max = MAX_PHOTOS) {
  const photos = photoSlots(row, max);
  if (!photos.length) return null;
  const requested = Number(row?.thumbnailPhoto || 0) - 1;
  return photos[Math.min(Math.max(requested, 0), photos.length - 1)];
}

export function withPhotos(payload, photos, thumbnailIndex, max = MAX_PHOTOS) {
  const result = { ...payload };
  for (let i = 0; i < max; i++) {
    result[photoSlotKey(i)] = photos[i] ?? null;
  }
  const index = photos.length ? Math.min(Math.max(thumbnailIndex, 0), photos.length - 1) : 0;
  result.thumbnailPhoto = photos.length ? index + 1 : null;
  return result;
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${file.name} is not a readable image.`));
    };
    image.src = url;
  });
}

function assertEncodable(dataUrl, name) {
  if (dataUrl.length > MAX_ENCODED_CHARS) {
    throw new Error(`${name} is too detailed to store; try a smaller image.`);
  }
  return dataUrl;
}

export async function encodePhoto(file) {
  if (!file?.type?.startsWith("image/")) {
    throw new Error(`${file?.name || "That file"} is not an image.`);
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error(`${file.name} is larger than ${MAX_SOURCE_BYTES / 1024 / 1024} MB.`);
  }
  if (file.type === "image/jpeg" && file.size <= PASSTHROUGH_BYTES) {
    return assertEncodable(await readAsDataUrl(file), file.name);
  }

  const image = await loadImage(file);
  const longest = Math.max(image.naturalWidth, image.naturalHeight);
  const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

  const context = canvas.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  return assertEncodable(canvas.toDataURL("image/jpeg", JPEG_QUALITY), file.name);
}

export async function encodePhotos(files, { max = MAX_PHOTOS, existing = 0 } = {}) {
  const accepted = files.slice(0, Math.max(0, max - existing));
  const skipped = files.length - accepted.length;
  const results = await Promise.allSettled(accepted.map(encodePhoto));

  const encoded = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
  const rejected = results.filter((r) => r.status === "rejected");
  const messages = [];

  if (rejected.length) {
    messages.push(rejected.length === 1 ? rejected[0].reason?.message || "One photo could not be read." : `${rejected.length} photos could not be read.`);
  }
  if (skipped > 0) {
    messages.push(`Only ${max} photos are allowed; skipped ${skipped} file${skipped === 1 ? "" : "s"}.`);
  }

  return { encoded, message: messages.join(" ") || null };
}
